import {
  leadsLogsListQueryKey,
  logsMeetingCreateMutation,
  opportunitiesLogsListOptions,
  PaginatedReturnUserListReadable,
  ReturnUserReadable,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { LogAction, LogType } from '@crm/types'
import { formatToIsoString, userTransformResults } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_API,
  format,
  showApiErrors
} from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { Button, Col, DatePicker, Flex, Form, Input, Row } from 'antd'
import { useParams } from 'react-router-dom'

type Props = {
  onCloseForm: () => void
}

export const MeetingForm = ({ onCloseForm }: Props) => {
  const { t } = useLocales()
  const { id: lead, opportunityId } = useParams()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { mutate: postMutation } = useMutation({
    ...logsMeetingCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => onSuccessMutation()
  })

  const onSuccessMutation = () => {
    invalidateQueries([
      leadsLogsListQueryKey({ path: { lead_id: String(lead) } })
    ])
    if (opportunityId) {
      invalidateQueries([
        opportunitiesLogsListOptions({
          path: { opportunity_id: String(opportunityId) }
        })
      ])
    }
    onCloseForm()
  }

  const handleSubmit = () => {
    const { meeting_on, ...data } = form.getFieldsValue()

    const body = {
      ...data,
      meeting_on: formatToIsoString(
        format(meeting_on, DATE_TIME_FORMAT_API, DATE_TIME_FORMAT)
      ),
      lead,
      action: LogAction.UPDATED,
      type: LogType.MEETING
    }

    postMutation({
      body
    })
  }

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Row gutter={8}>
        <Col span={8}>
          <Form.Item name="description" label={t('logs.create_event')}>
            <Input placeholder={t('logs.create_event')} />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="meeting_with" label={t('logs.who')}>
            <InfiniteSelect<
              UsersListData,
              PaginatedReturnUserListReadable,
              ReturnUserReadable
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={usersListInfiniteOptions}
              getOptions={({ options }) => userTransformResults(options)}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item name="meeting_on" label={t('logs.date_time')}>
            <DatePicker showTime format={DATE_TIME_FORMAT} className="w-full" />
          </Form.Item>
        </Col>
      </Row>

      <Flex justify="space-between">
        <Button onClick={onCloseForm}>{t('buttons.cancel')}</Button>

        <Button type="primary" htmlType="submit">
          {t('buttons.save')}
        </Button>
      </Flex>
    </Form>
  )
}
